import React, { useState, useEffect } from 'react';
import { Heart, CheckSquare, Settings } from 'lucide-react';
import { ThemeConfig, SpaceThing, FavoriteItem, Todo, CalendarEvent, Layouts, DashboardConfig } from '../types';
import MiniCalendar from './MiniCalendar';
import DashboardSettings from './DashboardSettings';
import { WidthProvider, Responsive, Layout as RglLayout } from 'react-grid-layout';
import { firebaseService } from '../FirebaseService';
import '/node_modules/react-grid-layout/css/styles.css';
import '/node_modules/react-resizable/css/styles.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

interface MySpaceDashboardProps {
  theme: ThemeConfig;
  spaceThings: SpaceThing[];
  favorites: FavoriteItem[];
  todos: Todo[];
  events: CalendarEvent[];
}

const MySpaceDashboard: React.FC<MySpaceDashboardProps> = ({ theme, spaceThings, favorites, todos, events }) => {
  const [dashboardConfig, setDashboardConfig] = useState<DashboardConfig | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const topTodos = todos.filter(t => !t.done).slice(0, 5);

  // When config exists, respect user's selection (even if empty)
  // When config doesn't exist, show defaults
  const displayedFavorites = dashboardConfig
    ? favorites.filter(fav => dashboardConfig.selectedFavorites.includes(fav.id.toString()))
    : favorites.slice(0, 3);

  const displayedThings = dashboardConfig
    ? spaceThings.filter(thing => dashboardConfig.selectedThings.includes(thing.id))
    : spaceThings.filter(t => t.image).slice(0, 4);

  const getDisplayImage = (fav: FavoriteItem): string | null => {
    return fav.useUnsplash ? fav.imageUrl : fav.localImageUrl;
  };


  useEffect(() => {
    const fetchDashboardConfig = async () => {
      let config = await firebaseService.getDashboardConfig();

      if (!config) {
        // Create a default config if none exists
        const defaultLayout: RglLayout[] = [
          ...displayedFavorites.map((f, i) => ({ i: `fav-${f.id}`, x: i * 3, y: 0, w: 3, h: 6 })),
          { i: 'calendar', x: 9, y: 0, w: 3, h: 8 },
          ...displayedThings.map((t, i) => ({ i: `thing-${t.id}`, x: (i % 3) * 3, y: 6, w: 3, h: 6 })),
          { i: 'todos', x: 0, y: 12, w: 12, h: 8 },
        ];
        config = {
          layouts: { lg: defaultLayout },
          visibleWidgets: {
            things: true,
            favorites: true,
          },
          selectedThings: displayedThings.map(t => t.id),
          selectedFavorites: displayedFavorites.map(f => f.id.toString()),
        };
      }
      
      // Deep copy to avoid downstream mutations, just in case
      const newConfig = JSON.parse(JSON.stringify(config));
      
      const lgLayout = newConfig.layouts.lg || [];
      const xsLayout = newConfig.layouts.xs || [];

       if (lgLayout.some((item: RglLayout) => item.w === undefined || item.h === undefined || item.x === undefined || item.y === undefined)) {
      }
      if (xsLayout.some((item: RglLayout) => item.w === undefined || item.h === undefined || item.x === undefined || item.y === undefined)) {
      }


      setDashboardConfig(newConfig);
    };

    fetchDashboardConfig().finally(() => {
      setIsInitialLoad(false);
    });
  }, [favorites.length, spaceThings.length]); // Re-run if the base data changes

  
  const onLayoutChange = async (currentLayout: RglLayout[], allLayouts: Layouts) => {
    if (isInitialLoad || !dashboardConfig) {
      return;
    }
    const currentUser = firebaseService.auth.currentUser;
    if (!currentUser) {
      console.warn('User not authenticated, skipping config save');
      return;
    }

    const updatedConfig: DashboardConfig = {
      ...dashboardConfig,
      layouts: allLayouts,
    };

    // Keep the state in sync
    setDashboardConfig(updatedConfig);

    try {
      await firebaseService.saveDashboardConfig(updatedConfig);
    } catch (error: any) {
      console.error('Error saving dashboard config:', error);
    }
  };
  const handleConfigChange = (newConfig: DashboardConfig) => {
    // Remove layout items for deselected things/favorites
    const cleanedLayouts: Layouts = {};
    
    Object.entries(newConfig.layouts).forEach(([breakpoint, layout]) => {
      cleanedLayouts[breakpoint] = layout.filter(item => {
        // Keep calendar and todos
        if (item.i === 'calendar' || item.i === 'todos') {
          return true;
        }
        
        // Keep things that are selected
        if (item.i.startsWith('thing-')) {
          const thingId = item.i.replace('thing-', '');
          return newConfig.selectedThings.includes(thingId);
        }
        
        // Keep favorites that are selected
        if (item.i.startsWith('fav-')) {
          const favId = item.i.replace('fav-', '');
          return newConfig.selectedFavorites.includes(favId);
        }
        
        return false;
      });
    });
    
    const updatedConfig = {
      ...newConfig,
      layouts: cleanedLayouts,
    };
    
    setDashboardConfig(updatedConfig);
    firebaseService.saveDashboardConfig(updatedConfig);
  };
  return (
    <div className="relative">
      <button
        onClick={() => setIsSettingsOpen(true)}
        className={`absolute top-0 right-0 m-4 p-2 rounded-full ${theme.secondary} hover:${theme.accent} transition-colors z-10`}
        aria-label="Dashboard Settings"
      >
        <Settings className={`${theme.text}`} />
      </button>
      <h2 className={`text-2xl font-bold ${theme.text} mb-6`}>
        Yuk's Space 🏡
      </h2>

      {dashboardConfig ? (
        <ResponsiveGridLayout
          className="layout"
          layouts={dashboardConfig.layouts}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
          cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
          rowHeight={30}
          onLayoutChange={onLayoutChange}
        >
          {dashboardConfig.visibleWidgets.things && displayedThings.map((thing) => (
            <div key={`thing-${thing.id}`} className={`${theme.cardBg} backdrop-blur-sm rounded-2xl p-4 shadow-lg ${theme.shadow} border ${theme.border}`}>
              <div className={`aspect-square ${theme.secondary} rounded-xl overflow-hidden`}>
                <img src={thing.image || ''} alt={thing.name} className="w-full h-full object-cover" />
              </div>
              {/* <h3 className={`font-semibold ${theme.text} text-center`}>{thing.name}</h3> */}
            </div>
          ))}
          
          {dashboardConfig.visibleWidgets.favorites && displayedFavorites.map((fav) => {
            const displayImage = getDisplayImage(fav);
            return (
              <div key={`fav-${fav.id}`} className={`${theme.cardBg} backdrop-blur-sm rounded-2xl p-4 shadow-lg ${theme.shadow} border ${theme.border}`}>
                <div className={`aspect-square ${theme.secondary} rounded-xl overflow-hidden flex items-center justify-center`}>
                  {displayImage ? (
                    <img src={displayImage} alt={fav.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Heart className={`w-1/3 h-1/3 ${theme.textLight}`} />
                    </div>
                  )}
                </div>
                {/* <h3 className={`font-semibold ${theme.text} text-center`}>{fav.name}</h3> */}
              </div>
            );
          })}
          
          <div key="calendar">
            <MiniCalendar theme={theme} events={events} />
          </div>
          
          <div key="todos" className="relative h-full bg-no-repeat bg-contain bg-bottom" style={{ backgroundImage: "url('/panda.jpg')" }}>
            <div className="absolute top-0 left-0 right-0 p-4">
            <h3 className={`font-bold ${theme.text} text-center mb-3 flex items-center justify-center gap-2`}>
              <CheckSquare className="w-5 h-5" />
              To-Dos
            </h3>
            <div className="max-w-xs mx-auto">
              {topTodos.length > 0 ? (
                <div className="space-y-2">
                  {topTodos.map(todo => (
                    <div key={todo.id} className={`flex items-center gap-2 py-2 px-3 ${theme.secondary} bg-opacity-80 backdrop-blur-sm rounded-lg`}>
                      <CheckSquare className={`w-4 h-4 ${theme.textLight}`} />
                      <span className={`${theme.text} text-sm`}>{todo.text}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={`${theme.textLight} text-sm text-center py-4`}>No pending tasks! 🎉</p>
              )}
            </div>
          </div>
        </div>
        </ResponsiveGridLayout>
      ) : (
        <p className={`${theme.text} text-center`}>Loading your space...</p>
      )}
      {dashboardConfig && (
        <DashboardSettings
          theme={theme}
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          config={dashboardConfig}
          onConfigChange={handleConfigChange}
          allThings={spaceThings}
          allFavorites={favorites}
        />
      )}
    </div>
  );
};

export default MySpaceDashboard;